import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useForm, type UseFormReturn, useWatch } from 'react-hook-form';
import type { AutoCompleteOption } from 'src/components/Forms/types';

type ModalStep = 'actions' | 'update-config';

export enum BulkEditorFormField {
  Value = 'value',
  EntityOption = 'entityOption',
  SnoozeUntil = 'snoozeUntil',
}

export interface BulkEditorFormValues {
  [BulkEditorFormField.Value]: string;
  [BulkEditorFormField.EntityOption]: AutoCompleteOption | null;
  [BulkEditorFormField.SnoozeUntil]: string;
}

const DEFAULT_FORM_VALUES: BulkEditorFormValues = {
  [BulkEditorFormField.Value]: '',
  [BulkEditorFormField.EntityOption]: null,
  [BulkEditorFormField.SnoozeUntil]: '',
};

interface UseBulkActionsModalStateParams {
  open: boolean;
  initialUpdateAttribute: string | null;
  availableAttributes: string[];
}

interface UseBulkActionsModalStateResult {
  step: ModalStep;
  setStep: (step: ModalStep) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedAttribute: string;
  setSelectedAttribute: (value: string) => void;
  formMethods: UseFormReturn<BulkEditorFormValues>;
  editorValue: string;
  setEditorValue: (value: string) => void;
  multiSelectValues: string[];
  setMultiSelectValues: Dispatch<SetStateAction<string[]>>;
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
  snoozeUntilValue: string;
  setSnoozeUntilValue: (value: string) => void;
  resetFormState: () => void;
  openUpdateConfig: (attributeKey: string) => void;
  resetToActions: () => void;
}

export function useBulkActionsModalState({
  open,
  initialUpdateAttribute,
  availableAttributes,
}: UseBulkActionsModalStateParams): UseBulkActionsModalStateResult {
  const [step, setStep] = useState<ModalStep>('actions');
  const [searchValue, setSearchValue] = useState('');
  const [selectedAttribute, setSelectedAttribute] = useState<string>('');
  const formMethods = useForm<BulkEditorFormValues>({ defaultValues: DEFAULT_FORM_VALUES });
  const { control, reset, setValue } = formMethods;
  const editorValue = useWatch({ control, name: BulkEditorFormField.Value });
  const snoozeUntilValue = useWatch({ control, name: BulkEditorFormField.SnoozeUntil });
  const [multiSelectValues, setMultiSelectValues] = useState<string[]>([]);
  const [searchInputValue, setSearchInputValue] = useState('');

  const setEditorValue = useCallback(
    (value: string) => setValue(BulkEditorFormField.Value, value),
    [setValue],
  );
  const setSnoozeUntilValue = useCallback(
    (value: string) => setValue(BulkEditorFormField.SnoozeUntil, value),
    [setValue],
  );

  const resetFormState = useCallback(() => {
    setSelectedAttribute('');
    reset(DEFAULT_FORM_VALUES);
    setMultiSelectValues([]);
    setSearchInputValue('');
    setSearchValue('');
  }, [reset]);

  const openUpdateConfig = useCallback(
    (attributeKey: string) => {
      setSearchValue('');
      setSelectedAttribute(attributeKey);
      reset(DEFAULT_FORM_VALUES);
      setMultiSelectValues([]);
      setSearchInputValue('');
      setStep('update-config');
    },
    [reset],
  );

  const resetToActions = useCallback(() => {
    setStep('actions');
    resetFormState();
  }, [resetFormState]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (
      initialUpdateAttribute &&
      availableAttributes.some((attribute) => attribute === initialUpdateAttribute)
    ) {
      openUpdateConfig(initialUpdateAttribute);
      return;
    }

    resetToActions();
  }, [availableAttributes, initialUpdateAttribute, open, openUpdateConfig, resetToActions]);

  return {
    step,
    setStep,
    searchValue,
    setSearchValue,
    selectedAttribute,
    setSelectedAttribute,
    formMethods,
    editorValue,
    setEditorValue,
    multiSelectValues,
    setMultiSelectValues,
    searchInputValue,
    setSearchInputValue,
    snoozeUntilValue,
    setSnoozeUntilValue,
    resetFormState,
    openUpdateConfig,
    resetToActions,
  };
}
